import {
  Button,
  TextInput,
  Flex,
  useMantineTheme,
  Stack,
  NativeSelect,
} from "@mantine/core";
import { ReactElement, useCallback, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { customerStore } from "@stores/customer";
import { DEFAULT_CUSTOMER_ADDRESS } from "@stores/customer/constants";
import SelectCountry from "@components/ui/form/SelectCountry";
import Form from "@common/components/ui/form/Form";
import { showNotification } from "@mantine/notifications";
import Tick from "@common/components/icons/Tick";
import ErrorMessage from "@common/components/text/ErrorMessage";
import {
  getSelectItemsFromCountry,
  isAddressValidated,
  isStateFieldNeeded,
} from "@common/utils/data";
import { IAddress } from "@common/utils/string/types";

export default function BillingAddressForm(): ReactElement {
  const theme = useMantineTheme();
  const [theLoading, setTheLoading] = useState(false);

  // customer
  const {
    isLoading: loadingCustomerDetails,
    data: customerDetails,
    error: customerDetailsError,
  } = customerStore.useGetDetails();
  const {
    mutate: updateBillingAddress,
    isPending: loadingBillingAddress,
    error: billingAddressError,
  } = customerStore.useUpdateBillingAddress();
  const theError = customerDetailsError || billingAddressError || null;

  // Form
  const {
    register,
    control,
    watch,
    formState: { errors, isValid },
    handleSubmit,
  } = useForm<IAddress>({
    shouldFocusError: true,
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    defaultValues: customerDetails?.billing_address || DEFAULT_CUSTOMER_ADDRESS,
  });
  const watchCountry = watch("country");

  const handleStripeSubmit = useCallback(
    (billing_address: IAddress) => {
      setTheLoading(true);
      updateBillingAddress(
        { billing_address },
        {
          onSuccess() {
            setTheLoading(false);
            showNotification({
              color: "green.2",
              icon: <Tick color="white" />,
              autoClose: 5000,
              message: "Billing address updated.",
            });
          },

          onError() {
            setTheLoading(false);
          },
        },
      );
    },
    [updateBillingAddress],
  );

  const showSpinner =
    loadingCustomerDetails || loadingBillingAddress || theLoading;

  return (
    <Form
      data-testid="BillingAddressForm"
      onSubmit={handleSubmit(handleStripeSubmit)}
      disabled={showSpinner}
    >
      <Stack spacing="sm">
        <Flex
          align="flex-end"
          direction={{ base: "column", xs: "row" }}
          justify="flex-start"
          gap={theme.spacing.sm}
          w="100%"
        >
          <TextInput
            w="100%"
            id="street"
            label="Street address"
            placeholder="123 Main St"
            required
            {...register("street", { required: true })}
          />
          <TextInput
            w="100%"
            id="street2"
            placeholder="Unit number (optional)"
            {...register("street2")}
          />
        </Flex>

        <Controller
          name="country"
          control={control}
          rules={{ required: true }}
          render={({ field: { value, onChange } }) => (
            <SelectCountry
              data-testid="BillingAddressForm-country"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              mt={0}
            />
          )}
        />

        <Flex
          align="flex-start"
          direction={{ base: "column", xs: "row" }}
          justify="flex-start"
          gap={theme.spacing.lg}
          w="100%"
        >
          <TextInput
            w="100%"
            id="city"
            label="City"
            placeholder="City"
            defaultValue=""
            required
            {...register("city", { required: true })}
          />

          {isStateFieldNeeded(watchCountry) &&
            (isAddressValidated(watchCountry) ? (
              <Controller
                name="region"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <NativeSelect
                    w="100%"
                    data-testid="BillingAddressForm-region"
                    label="State"
                    data={getSelectItemsFromCountry(watchCountry)}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                  />
                )}
              />
            ) : (
              <TextInput
                w="100%"
                id="region"
                label="State"
                placeholder="State"
                {...register("region")}
              />
            ))}

          <TextInput
            w="100%"
            id="zip"
            label="ZIP Code"
            placeholder="Zipcode"
            maxLength={20}
            required
            {...register("zip", {
              required: true,
            })}
          />
        </Flex>
        {errors.zip && <ErrorMessage>{errors.zip.message}</ErrorMessage>}

        {theError && (
          <ErrorMessage m={0}>
            {theError.message || theError.status}
          </ErrorMessage>
        )}
      </Stack>

      <Button
        type="submit"
        loading={showSpinner}
        loaderPosition="right"
        disabled={!isValid || showSpinner}
        mt="lg"
      >
        Save
      </Button>
    </Form>
  );
}
